import React, { ChangeEventHandler, useCallback } from 'react';
import { FunctionComponent } from 'react';
import { Field } from '../../Field/Field';
import { Input, FieldContainer } from '../../Components';
import { Validator } from '../../Field';

export type NumberFieldProps = {
    id?: string;
    name: string;
    label: string;
    description?: string;
    initialValue?: number;
    step?: number;
    min?: number;
    max?: number;
    required?: boolean;
    disabled?: boolean;
};

export const NumberField: FunctionComponent<NumberFieldProps> = ({
    id,
    name,
    label,
    description,
    initialValue,
    step,
    min,
    max,
    required = false,
    disabled,
}) => {
    const validate = useCallback<Validator<number>>(
        async (value) => {
            const errorMessage = validateValue({ min, max, value });

            if (errorMessage) {
                return { pass: false, message: errorMessage };
            }

            return { pass: true };
        },
        [step, min, max]
    );

    return (
        <Field<number>
            id={id}
            name={name}
            description={description}
            initialValue={initialValue}
            onBlurValidate={validate}
            onSubmitValidate={validate}
            required={required}
        >
            {({
                fieldId,
                value,
                setValue,
                onBlur,
                errors,
                errorId,
                descriptionId,
                isValid,
                required,
                requiredStrategy,
            }) => {
                const onChange = (value: string | null) => {
                    if (value === null) {
                        return setValue(null);
                    }
                    if (value === '') {
                        return setValue(null);
                    }
                    setValue(parseFloat(value));
                };

                return (
                    <FieldContainer
                        errorId={errorId}
                        errors={errors}
                        descriptionId={descriptionId}
                        description={description}
                    >
                        <Input
                            id={fieldId}
                            type="number"
                            name={name}
                            step={step}
                            min={min}
                            max={max}
                            label={label}
                            value={String(value)}
                            onChange={onChange}
                            onBlur={onBlur}
                            aria-invalid={!isValid}
                            aria-errormessage={errorId}
                            aria-describedby={descriptionId}
                            required={required}
                            requiredStrategy={requiredStrategy}
                            disabled={disabled}
                        />
                    </FieldContainer>
                );
            }}
        </Field>
    );
};

type ValidationInput = {
    value: number;
    min?: number;
    max?: number;
};

export const validateValue = ({ value, min, max }: ValidationInput): string | void => {
    if (min && value < min) {
        return `Must be greater than or equal to ${min}`;
    }

    if (max && value > max) {
        return `Must be less than or equal to ${max}`;
    }
};
